import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../allauth/auth';
import ApiClient from '../config/ApiClient';
import { PaymentAssignments } from '../object-actions/types/types';
import {
    canEditAssignment,
    canRemoveCoach,
    canRemoveParent,
} from '../utils/assignmentPermissions';
import { resolveTenantPath } from '../utils/tenantUrl';

export function useAssignmentActions(
    assignment: PaymentAssignments,
    onUpdate: () => void,
    isPayer?: boolean,
) {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const auth = useAuth();
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [itemToRemove, setItemToRemove] = useState<{
        type: 'coach' | 'parent';
        id: number | string;
        name: string;
    } | null>(null);

    const currentUser = auth?.data?.user;
    const userId = currentUser?.id;

    const userRole = isPayer ? 'payer' : (
        assignment.payment?.entity?.author?.id === userId ? 'payer' : null
    );

    const canEdit = canEditAssignment(userId, assignment, userRole);
    const isSubmitted = !!(assignment.pre_assessment_submitted_at || assignment.post_assessment_submitted_at ||
        assignment.pre_assessment_submitted || assignment.post_assessment_submitted);

    const assignmentId = typeof assignment.id === 'number'
        ? assignment.id
        : parseInt(String(assignment.id), 10);

    const editPath = `/account/payments/${assignmentId}`;
    const editTarget = resolveTenantPath(assignment.organization?.slug, editPath);

    const handleEditClick = () => {
        if (!canEdit) {
            enqueueSnackbar('You do not have permission to edit this assignment', { variant: 'error' });
            return;
        }
        if (editTarget.external) {
            window.open(editTarget.href, '_blank', 'noopener,noreferrer');
            return;
        }
        navigate(editTarget.href);
    };

    const handleRemoveClick = (type: 'coach' | 'parent', id: number | string, name: string) => {
        const canRemove = type === 'coach'
            ? canRemoveCoach(userId, assignment, id, userRole)
            : canRemoveParent(userId, assignment, id, userRole);

        if (!canRemove) {
            enqueueSnackbar(`You do not have permission to remove this ${type}`, { variant: 'error' });
            return;
        }

        setItemToRemove({ type, id, name });
        setRemoveDialogOpen(true);
    };

    const handleRemoveConfirm = async () => {
        if (!itemToRemove) return;

        try {
            const updateData: Record<string, number[]> = {};

            if (itemToRemove.type === 'coach') {
                updateData.coaches = (assignment.coaches ?? [])
                    .filter(coach => coach.id !== itemToRemove.id)
                    .map(coach => coach.id as number);
            } else {
                updateData.parents = (assignment.parents ?? [])
                    .filter(parent => parent.id !== itemToRemove.id)
                    .map(parent => parent.id as number);
            }

            const response = await ApiClient.patch(`/api/payment-assignments/${assignmentId}/`, updateData);

            if (response.success) {
                enqueueSnackbar(`${itemToRemove.type} removed successfully`, { variant: 'success' });
                onUpdate();
            } else {
                enqueueSnackbar(response.error || 'Failed to remove item', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error removing item:', error);
            enqueueSnackbar('Failed to remove item', { variant: 'error' });
        } finally {
            setRemoveDialogOpen(false);
            setItemToRemove(null);
        }
    };

    return {
        userId,
        userRole,
        canEdit,
        isSubmitted,
        removeDialogOpen,
        setRemoveDialogOpen,
        itemToRemove,
        editTarget,
        handleEditClick,
        handleRemoveClick,
        handleRemoveConfirm,
    };
}
